import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRevalidator } from '@remix-run/react';

import type { CommunityProfileField, ProfileFieldErrors, ProfileFieldValue } from '~/interfaces/community';
import type { NectarPageBlock } from '~/utils/nectarPageBlock';
import toast from '~/utils/toast';

import { initialEditableAnswers, pageAnswersPayload, validatePageAnswers } from '../_utils/onboardingProfileFields';
import useUpdateProfileFieldsMutation, { ProfileFieldsRequestError } from './useUpdateProfileFieldsMutation';

type UseProfileQuestionAnswersProps = {
  fields: CommunityProfileField[];
  currentPage?: CommunityProfileField[];
  errorDisplay?: 'toast' | 'inline';
  pageBlock?: NectarPageBlock;
};

type ProfileAnswerDraft = {
  customFieldId: string | null;
  value: ProfileFieldValue;
};

const useProfileQuestionAnswers = ({
  fields,
  currentPage = fields,
  errorDisplay = 'toast',
  pageBlock,
}: UseProfileQuestionAnswersProps) => {
  const { t } = useTranslation();
  const { revalidate } = useRevalidator();
  const [answerDrafts, setAnswerDrafts] = useState<Record<string, ProfileAnswerDraft>>({});
  const answers = useMemo(() => {
    const next = initialEditableAnswers(fields);
    for (const field of fields) {
      const draft = answerDrafts[field.id];
      if (draft?.customFieldId === field.custom_field_id) next[field.id] = draft.value;
    }
    return next;
  }, [answerDrafts, fields]);
  const [fieldErrors, setFieldErrors] = useState<ProfileFieldErrors>({});
  const [answersErrorMessage, setAnswersErrorMessage] = useState<string | null>(null);
  const { mutate: saveAnswers, isPending } = useUpdateProfileFieldsMutation();
  const visibleQuestionIds = useMemo(() => new Set(currentPage.map((field) => field.id)), [currentPage]);
  const formError = Object.keys(fieldErrors).some((fieldId) => !visibleQuestionIds.has(fieldId))
    ? answersErrorMessage
    : null;

  useEffect(() => {
    if (errorDisplay === 'inline') return;
    const staleIds = Object.keys(fieldErrors).filter((fieldId) => !visibleQuestionIds.has(fieldId));
    if (staleIds.length === 0) return;

    if (answersErrorMessage) toast.error(answersErrorMessage);
    setFieldErrors((previous) => {
      const next = { ...previous };
      for (const fieldId of staleIds) delete next[fieldId];
      return next;
    });
  }, [answersErrorMessage, errorDisplay, fieldErrors, visibleQuestionIds]);

  const handleAnswerChange = (fieldId: string, value: ProfileFieldValue) => {
    const field = fields.find((entry) => entry.id === fieldId);
    if (!field) return;

    setAnswerDrafts((previous) => ({ ...previous, [fieldId]: { customFieldId: field.custom_field_id, value } }));
    if (fieldErrors[fieldId]) {
      setFieldErrors((previous) => {
        const next = { ...previous };
        delete next[fieldId];
        return next;
      });
    }
  };

  const handleAnswerError = (error: Error) => {
    revalidate();

    const fieldLevelErrors = error instanceof ProfileFieldsRequestError ? error.fieldErrors : {};
    if (Object.keys(fieldLevelErrors).length > 0) {
      setFieldErrors(fieldLevelErrors);
      setAnswersErrorMessage(error.message);
      return;
    }
    toast.error(error.message);
  };

  const validateAnswers = (fields: CommunityProfileField[]) => {
    const nextErrors = validatePageAnswers(fields, answers, t('community.onboard.required'));
    setFieldErrors(nextErrors);
    setAnswersErrorMessage(null);
    return Object.keys(nextErrors).length === 0;
  };

  const submitAnswers = (fields: CommunityProfileField[], onSuccess: () => void) => {
    saveAnswers(
      { answers: pageAnswersPayload(fields, answers), pageBlock },
      {
        onSuccess,
        onError: handleAnswerError,
      },
    );
  };

  return { answers, fieldErrors, formError, handleAnswerChange, validateAnswers, submitAnswers, isPending };
};

export default useProfileQuestionAnswers;
